import React, { useEffect, useState, useCallback, useRef } from 'react';
import { t } from '../../i18n';
import { getExtensionVersion } from '../../extensionApi';
import './style.scss';

export interface Category {
  name: string;
  domains: string[];
  color: string;
}

const STORAGE_KEY = 'bls_categories';
const CATEGORY_COLORS = ['#1976d2', '#388e3c', '#7b1fa2', '#d32f2f', '#f57c00', '#00897b', '#5c6bc0', '#c2185b'];

function loadCategoriesFromLocalStorage(): Category[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function loadCategoriesFromExtension(): Promise<Category[]> {
  return new Promise((resolve) => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'CATEGORY_DATA_RESPONSE') {
        window.removeEventListener('message', handleMessage);
        resolve(event.data.success && Array.isArray(event.data.data) ? event.data.data : []);
      }
    };
    window.addEventListener('message', handleMessage);
    window.postMessage({ type: 'CATEGORY_GET_DATA' }, '*');
    setTimeout(() => { window.removeEventListener('message', handleMessage); resolve([]); }, 3000);
  });
}

export function saveCategoriesToExtension(categories: Category[]): Promise<boolean> {
  return new Promise((resolve) => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'CATEGORY_UPDATE_RESPONSE') {
        window.removeEventListener('message', handleMessage);
        resolve(event.data.success);
      }
    };
    window.addEventListener('message', handleMessage);
    window.postMessage({ type: 'CATEGORY_UPDATE_DATA', categories }, '*');
    setTimeout(() => { window.removeEventListener('message', handleMessage); resolve(false); }, 3000);
  });
}

function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

// localStorage -> chrome.storage マイグレーション (既存ユーザー対応)
async function migrateCategories(): Promise<Category[]> {
  const localData = loadCategoriesFromLocalStorage();
  console.log('[BLS] migrate: localStorage =', localData.length, 'items', localData.map(c => c.name));
  if (localData.length === 0) {
    console.log('[BLS] migrate: localStorage empty, loading from extension');
    const ext = await loadCategoriesFromExtension();
    console.log('[BLS] migrate: extension =', ext.length, 'items');
    return ext;
  }

  const version = await getExtensionVersion();
  console.log('[BLS] migrate: extension version =', version);
  if (!version || compareVersions(version, '2.6.0') < 0) {
    console.log('[BLS] migrate: skip (version not ready)');
    return localData;
  }

  const extensionData = await loadCategoriesFromExtension();
  console.log('[BLS] migrate: extension data =', extensionData.length, 'items');

  if (extensionData.length === 0) {
    const saved = await saveCategoriesToExtension(localData);
    console.log('[BLS] migrate: saved to extension =', saved);
    if (saved) {
      localStorage.removeItem(STORAGE_KEY);
      console.log('[BLS] migrate: localStorage cleared');
    }
    return localData;
  }

  localStorage.removeItem(STORAGE_KEY);
  console.log('[BLS] migrate: using extension data, localStorage cleared');
  return extensionData;
}

// 後方互換: CategoryStats の初期表示用フォールバック
export function loadCategories(): Category[] {
  return loadCategoriesFromLocalStorage();
}

const CategoryManager: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [newDomainInputs, setNewDomainInputs] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const [knownDomains, setKnownDomains] = useState<string[]>([]);
  const [activeSuggest, setActiveSuggest] = useState<number | null>(null);
  const suggestRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const saveCategories = useCallback((cats: Category[]) => {
    console.log('[BLS] save:', cats.length, 'items');
    setCategories(cats);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cats));
    saveCategoriesToExtension(cats);
  }, []);

  useEffect(() => {
    const localData = loadCategoriesFromLocalStorage();
    console.log('[BLS] init: localStorage loaded', localData.length, 'items');
    if (localData.length > 0) {
      setCategories(localData);
    } else {
      loadCategoriesFromExtension().then(extData => {
        console.log('[BLS] init: extension loaded', extData.length, 'items');
        if (extData.length > 0) {
          setCategories(extData);
        }
      });
    }
    getExtensionVersion().then(v => console.log('[BLS] extension version:', v));
    migrateCategories();
  }, []);

  // 閲覧履歴からドメイン一覧を取得
  const loadKnownDomains = useCallback(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'VIEWING_TIME_DATA_RESPONSE') {
        window.removeEventListener('message', handleMessage);
        if (event.data.success && event.data.data) {
          setKnownDomains(Object.keys(event.data.data).sort());
        }
      }
    };
    window.addEventListener('message', handleMessage);
    window.postMessage({ type: 'VIEWING_TIME_GET_DATA' }, '*');
    setTimeout(() => window.removeEventListener('message', handleMessage), 3000);
  }, []);

  const loadKnownDomainsFromStorage = useCallback(() => {
    try {
      if (window.chrome?.storage?.local) {
        window.chrome.storage.local.get(['viewing_time_data'], (result: any) => {
          if (chrome.runtime.lastError) return;
          if (result.viewing_time_data) {
            setKnownDomains(Object.keys(result.viewing_time_data).sort());
          }
        });
      }
    } catch {}
  }, []);

  useEffect(() => {
    const handleExtensionReady = (event: MessageEvent) => {
      if (event.data.type === 'BLACKLIST_EXTENSION_READY') {
        loadKnownDomains();
      }
    };
    window.addEventListener('message', handleExtensionReady);

    setTimeout(() => {
      loadKnownDomains();
      loadKnownDomainsFromStorage();
    }, 500);

    return () => window.removeEventListener('message', handleExtensionReady);
  }, [loadKnownDomains, loadKnownDomainsFromStorage]);

  // サジェスト外クリックで閉じる
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (activeSuggest === null) return;
      const ref = suggestRefs.current[activeSuggest];
      if (ref && !ref.contains(e.target as Node)) {
        setActiveSuggest(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeSuggest]);

  const getSuggestions = (catIndex: number): string[] => {
    const input = (newDomainInputs[catIndex] || '').trim().toLowerCase();
    if (!input) return [];
    const cat = categories[catIndex];
    const allAssigned = categories.flatMap(c => c.domains);
    return knownDomains
      .filter(d => !cat.domains.includes(d) && !allAssigned.includes(d) && d.includes(input))
      .sort((a, b) => {
        const aExact = a === input ? 0 : a.startsWith(input) ? 1 : 2;
        const bExact = b === input ? 0 : b.startsWith(input) ? 1 : 2;
        if (aExact !== bExact) return aExact - bExact;
        return a.localeCompare(b);
      });
  };

  const addCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;
    if (categories.some(c => c.name === name)) {
      setError(t('catErrorDuplicate'));
      return;
    }
    const color = CATEGORY_COLORS[categories.length % CATEGORY_COLORS.length];
    saveCategories([...categories, { name, domains: [], color }]);
    setNewCategoryName('');
    setError('');
  };

  const deleteCategory = (index: number) => {
    saveCategories(categories.filter((_, i) => i !== index));
    setEditingIndex(null);
  };

  const startEditName = (index: number) => {
    setEditingIndex(index);
    setEditName(categories[index].name);
  };

  const saveEditName = (index: number) => {
    const name = editName.trim();
    if (!name) return;
    if (categories.some((c, i) => i !== index && c.name === name)) {
      setError(t('catErrorDuplicate'));
      return;
    }
    const updated = [...categories];
    updated[index] = { ...updated[index], name };
    saveCategories(updated);
    setEditingIndex(null);
    setError('');
  };

  const extractDomain = (input: string): string => {
    try {
      if (input.includes('://')) {
        return new URL(input).hostname;
      }
      if (input.includes('/')) {
        return new URL('https://' + input).hostname;
      }
    } catch {}
    return input;
  };

  const addDomainToCategory = (catIndex: number, domainOverride?: string) => {
    const domain = extractDomain((domainOverride || newDomainInputs[catIndex] || '').trim().toLowerCase());
    if (!domain) return;
    const cat = categories[catIndex];
    if (cat.domains.includes(domain)) {
      setError(t('catErrorDomainDuplicate'));
      return;
    }
    const updated = [...categories];
    updated[catIndex] = { ...cat, domains: [...cat.domains, domain] };
    saveCategories(updated);
    setNewDomainInputs({ ...newDomainInputs, [catIndex]: '' });
    setActiveSuggest(null);
    setError('');
  };

  const removeDomainFromCategory = (catIndex: number, domain: string) => {
    const updated = [...categories];
    updated[catIndex] = {
      ...updated[catIndex],
      domains: updated[catIndex].domains.filter(d => d !== domain),
    };
    saveCategories(updated);
  };

  const changeColor = (catIndex: number, color: string) => {
    const updated = [...categories];
    updated[catIndex] = { ...updated[catIndex], color };
    saveCategories(updated);
  };

  return (
    <div className="category-manager-container">
      <h2 className="category-title">{t('catTitle')}</h2>
      <p className="category-description">{t('catDescription')}</p>

      {error && (
        <div className="error-message">
          {error}
          <button className="error-dismiss" onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="add-category-form">
        <input
          type="text"
          value={newCategoryName}
          onChange={e => setNewCategoryName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addCategory()}
          placeholder={t('catNamePlaceholder')}
          className="category-input"
        />
        <button onClick={addCategory} className="add-button" disabled={!newCategoryName.trim()}>
          {t('catAdd')}
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📁</div>
          <p className="empty-title">{t('catEmptyTitle')}</p>
          <p className="empty-description">{t('catEmptyDesc')}</p>
        </div>
      ) : (
        <div className="categories-list">
          {categories.map((cat, catIndex) => {
            const suggestions = activeSuggest === catIndex ? getSuggestions(catIndex) : [];

            return (
              <div key={catIndex} className="category-card">
                <div className="category-header">
                  <div className="category-name-section">
                    <span className="color-dot" style={{ backgroundColor: cat.color }} />
                    {editingIndex === catIndex ? (
                      <div className="edit-name-form">
                        <input
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveEditName(catIndex)}
                          className="edit-name-input"
                          autoFocus
                        />
                        <button onClick={() => saveEditName(catIndex)} className="save-name-button">{t('btnSave')}</button>
                        <button onClick={() => setEditingIndex(null)} className="cancel-button">{t('catCancel')}</button>
                      </div>
                    ) : (
                      <>
                        <span className="category-name">{cat.name}</span>
                        <span className="domain-count">{t('catDomainCount', { count: cat.domains.length })}</span>
                      </>
                    )}
                  </div>
                  <div className="category-actions">
                    <div className="color-picker">
                      {CATEGORY_COLORS.map(color => (
                        <button
                          key={color}
                          className={`color-option ${cat.color === color ? 'selected' : ''}`}
                          style={{ backgroundColor: color }}
                          onClick={() => changeColor(catIndex, color)}
                        />
                      ))}
                    </div>
                    {editingIndex !== catIndex && (
                      <button onClick={() => startEditName(catIndex)} className="icon-button edit">{t('catRename')}</button>
                    )}
                    <button onClick={() => deleteCategory(catIndex)} className="icon-button delete">{t('btnDelete')}</button>
                  </div>
                </div>

                <div className="domain-section">
                  <div className="add-domain-row" ref={el => { suggestRefs.current[catIndex] = el; }}>
                    <div className="domain-input-wrapper">
                      <input
                        type="text"
                        value={newDomainInputs[catIndex] || ''}
                        onChange={e => {
                          setNewDomainInputs({ ...newDomainInputs, [catIndex]: e.target.value });
                          setActiveSuggest(catIndex);
                        }}
                        onFocus={() => setActiveSuggest(catIndex)}
                        onKeyDown={e => e.key === 'Enter' && addDomainToCategory(catIndex)}
                        placeholder={t('catDomainPlaceholder')}
                        className="domain-input"
                      />
                      {activeSuggest === catIndex && suggestions.length > 0 && (
                        <div className="suggestions-dropdown">
                          {suggestions.map(domain => (
                            <button
                              key={domain}
                              className="suggestion-item"
                              onMouseDown={e => { e.preventDefault(); addDomainToCategory(catIndex, domain); }}
                            >
                              {domain}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => addDomainToCategory(catIndex)}
                      className="add-domain-button"
                      disabled={!(newDomainInputs[catIndex] || '').trim()}
                    >
                      {t('btnAdd')}
                    </button>
                  </div>

                  {cat.domains.length > 0 && (
                    <div className="domain-tags">
                      {cat.domains.map(domain => (
                        <span key={domain} className="domain-tag" style={{ borderColor: cat.color }}>
                          {domain}
                          <button className="remove-domain" onClick={() => removeDomainFromCategory(catIndex, domain)}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CategoryManager;
